import type { App, Plugin } from 'vue';
import tippy, { type Instance as TippyInstance, type Props as TippyProps } from 'tippy.js';
import TooltipException from '@/exceptions/TooltipException.js';

/**
 * Global tooltip plugin used as a Vue 3-compatible bridge for legacy `$tooltip` usage.
 *
 * Purpose:
 * - Preserve the historic imperative API (`init`, `setContent`, `destroy`, `getInstance`)
 *   used across legacy Options API screens (Schedule, Calendar, dashboard tables, etc.).
 * - Register the service through Vue 3 plugin patterns (`app.config.globalProperties` and
 *   `app.provide`) so both Options API and Composition API can access the same instance.
 * - Normalize native elements and jQuery-wrapped elements to keep compatibility during the
 *   Vue 2 -> Vue 3 migration.
 */
type TooltipTarget = TooltipElement | JQueryLikeObject | null | undefined;

type TooltipInitOptions = string | Partial<TippyProps> | undefined;

type JQueryConstructor = new (...args: unknown[]) => JQueryLikeObject;

interface TooltipElement extends Element {
    _tippy?: TippyInstance;
}

interface JQueryLikeObject {
    [index: number]: unknown;
    length?: number;
    jquery?: string;
}

export interface TooltipService {
    init(element: TooltipTarget, options?: TooltipInitOptions): TippyInstance | null;
    setContent(
        element: TooltipTarget,
        content: TippyProps['content'] | null | undefined
    ): TippyInstance | null;
    destroy(element: TooltipTarget): void;
    getInstance(element: TooltipTarget): TippyInstance | null;
}

const COMMENT_NODE = typeof Node !== 'undefined' ? Node.COMMENT_NODE : 8;

function getJQueryConstructor(): JQueryConstructor | null {
    const jq = (globalThis as { jQuery?: unknown }).jQuery;
    return typeof jq === 'function' ? (jq as JQueryConstructor) : null;
}

function isJQueryLikeObject(value: unknown): value is JQueryLikeObject {
    if (!value || typeof value !== 'object') {
        return false;
    }

    const jquery = value as JQueryLikeObject;
    const jqueryConstructor = getJQueryConstructor();

    if (jqueryConstructor && value instanceof jqueryConstructor) {
        return true;
    }

    return Boolean(jquery.jquery && jquery.length !== undefined && jquery[0]);
}

function normalizeElement(element: TooltipTarget): TooltipElement | null {
    if (!element) {
        return null;
    }

    if (isJQueryLikeObject(element)) {
        return (element[0] as TooltipElement | undefined) ?? null;
    }

    return element as TooltipElement;
}

function normalizeOptions(options?: TooltipInitOptions): Partial<TippyProps> {
    if (typeof options === 'string') {
        return { content: options };
    }

    return options ?? {};
}

export const tooltip: TooltipService = {
    init(element, options) {
        const normalizedElement = normalizeElement(element);

        if (!normalizedElement) {
            return null;
        }

        if (!normalizedElement.isConnected) {
            return null;
        }

        const normalizedOptions = normalizeOptions(options);

        if (normalizedElement.nodeType === COMMENT_NODE) {
            throw new TooltipException(
                'Cannot initialize tooltip on a comment node',
                'Make sure the element does not have a v-if directive that hides it',
                normalizedOptions
            );
        }

        const existingInstance = this.getInstance(normalizedElement);

        if (existingInstance) {
            existingInstance.setProps(normalizedOptions);
            return existingInstance;
        }

        return tippy(normalizedElement, {
            animation: 'fade',
            allowHTML: true,
            offset: [0, 5],
            ...normalizedOptions,
        }) as TippyInstance;
    },

    setContent(element, content) {
        const instance = this.getInstance(element);

        if (!instance) {
            return this.init(element, content);
        }

        if (!content) {
            instance.destroy();
        } else {
            instance.setContent(content);
        }

        return instance;
    },

    destroy(element) {
        const instance = this.getInstance(element);

        if (instance) {
            instance.destroy();
        }
    },

    getInstance(element) {
        const normalizedElement = normalizeElement(element);

        if (!normalizedElement) {
            return null;
        }

        return normalizedElement._tippy ?? null;
    },
};

const TippyPlugin: Plugin = {
    install(app: App): void {
        app.config.globalProperties.$tooltip = tooltip;
        app.provide('$tooltip', tooltip);
    },
};

export default TippyPlugin;
